import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React from 'react';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';
import { ClientCounterKind, pluralize } from './clientProgressFormat';

interface ClientCounterProps {
  kind: ClientCounterKind;
  count: number;
  /** `chip`: one line on the home card. `tile`: number over label on the detail header. */
  variant: 'chip' | 'tile';
  /** When set (and `variant` is `tile`) the tile is a button; otherwise it is static. */
  onClick?: () => void;
}

const TONE: Record<ClientCounterKind, string> = {
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  dueThisWeek:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
  awaitingApproval:
    'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100',
  openIncidents:
    'bg-white text-red-700 ring-1 ring-red-300 dark:bg-transparent dark:text-red-300 dark:ring-red-700',
};

const ICON: Record<ClientCounterKind, React.ElementType> = {
  overdue: AccessTimeOutlinedIcon,
  dueThisWeek: CalendarMonthOutlinedIcon,
  awaitingApproval: HourglassEmptyOutlinedIcon,
  openIncidents: WarningAmberOutlinedIcon,
};

const CHIP_BASE: Record<ClientCounterKind, string> = {
  overdue: 'overdue',
  dueThisWeek: 'dueThisWeek',
  awaitingApproval: 'awaitingApproval',
  openIncidents: 'openIncident',
};

const TILE_LABEL: Record<
  ClientCounterKind,
  | 'tileOverdue'
  | 'tileDueThisWeek'
  | 'tileAwaitingApproval'
  | 'tileOpenIncidents'
> = {
  overdue: 'tileOverdue',
  dueThisWeek: 'tileDueThisWeek',
  awaitingApproval: 'tileAwaitingApproval',
  openIncidents: 'tileOpenIncidents',
};

/**
 * Sprint 44, story-226. One read-only counter. Colour is never the only signal: every counter has
 * an icon, the number and words. Count and words only, never a task title or a money value.
 */
export const ClientCounter: React.FC<ClientCounterProps> = ({
  kind,
  count,
  variant,
  onClick,
}) => {
  const t = useTranslation('clientProgress');
  const Icon = ICON[kind];
  const testId = `client-counter-${kind}`;

  if (variant === 'chip') {
    return (
      <span
        data-testid={testId}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
          TONE[kind],
        )}>
        <Icon sx={{ fontSize: 14 }} aria-hidden="true" />
        {pluralize(t, CHIP_BASE[kind], count)}
      </span>
    );
  }

  const body = (
    <>
      <span className="flex items-center gap-1.5 text-2xl font-bold leading-none">
        <Icon sx={{ fontSize: 20 }} aria-hidden="true" />
        {count}
      </span>
      <span className="text-xs font-medium">{t[TILE_LABEL[kind]]}</span>
    </>
  );
  const tileClass = cn(
    'flex flex-col items-start gap-1 rounded-md px-3 py-2 min-w-[7rem] text-left',
    TONE[kind],
  );

  return onClick ? (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        tileClass,
        'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400',
      )}>
      {body}
    </button>
  ) : (
    <div data-testid={testId} className={tileClass}>
      {body}
    </div>
  );
};

export default ClientCounter;
